import { fromBech32 } from "@cosmjs/encoding";
import { instantiate2Address, signers } from "rujira.js";
import {
  base64Decode,
  base64Encode,
  bytesToHex,
  hexToBytes,
  utf8,
} from "./encoding";
import { canonicalCalls, type V2Call, type V2Coin } from "./action";
import type { PreparedV2Execution } from "./operations";
import {
  createContractRecoverySource,
  createResilientRecoverySource,
} from "./source";
import type { RecoverySource } from "./recovery";
import type { V2DeploymentConfig } from "./config";
import { parseCallRelayerFeeCaps } from "./safety";
import { averageBlockTimeMs } from "./operationSteps";
import {
  PrivateReadHttpError,
  readPrivateJson,
  retryTransientRead,
} from "./readJson";
import {
  assertFeeQueryAddress,
  parseFeeRules,
  validateCallFeeQuote,
  type V2CallFeeQuote,
  type V2FeeRules,
} from "./feeRules";

export type V2ContractConfig = {
  subwallet_code_id: number;
  subwallet_code_checksum: string;
  fee_collector: string;
  protocol_fee_bps: number;
  /** Absent on deployments without adjustable, proof-bound protocol fees. */
  fee_policy_version?: number;
  pending_protocol_fee_update?: {
    protocol_fee_bps: number;
    activation_height: number;
  } | null;
  maximum_protocol_fee_bps?: number;
  min_fee_update_delay_blocks?: number;
  /** Non-null rules activate contract-classified, aggregate-rounded fees. */
  fee_rules?: V2FeeRules | null;
  pending_fee_rules_update?: {
    rules: V2FeeRules;
    activation_height: number;
  } | null;
  call_relayer_fee_caps?: V2Coin[];
  registration_fee: V2Coin;
  root_history_size: number;
  max_ciphertext_bytes: number;
  max_expiry_delta: number;
  protocol_version: 2;
};

/** One of the relayer's signers; each sends its own transactions in order. */
export type V2RelayerSigner = {
  address: string;
  /** Accepts new relays right now. */
  available: boolean;
  /** The relayer's estimate for this signer's queue. */
  waitSeconds: number;
};

export type V2RelayerMetadata = {
  /** The signer this operation's proofs bind (see chooseRelayer). */
  address: string;
  /** The relayer's primary signer. Fee terms bind the relayer, not a signer. */
  primaryAddress?: string;
  /** Present when the relayer has several signers, the primary first. */
  signers?: readonly V2RelayerSigner[];
  minimumFees: ReadonlyMap<string, bigint>;
  sponsoredActions: ReadonlySet<string>;
  maxMessageBytes: number;
  maxCalls: number;
  /** The relayer sends a funding transfer and its call in one transaction. */
  fundingBundles?: boolean;
  registrationFee?: V2Coin;
  registrationSponsorAddress?: string;
};

export const REGISTRATION_SPONSOR_UNAVAILABLE =
  "Account creation is temporarily unavailable. Please try again.";

/** The proxy's public Reserve total for one denom (`reserve_liability`). */
export type V2ReserveLiability = {
  denom: string;
  /** Sum of every unspent reserve note of this denom (all users). */
  liability: bigint;
  /** The proxy's bank balance of this denom. */
  balance: bigint;
  solvent: boolean;
};

/**
 * One read of the Reserve total of every denom the proxy holds. The same
 * public requests for every user: the denom list is the proxy's own bank
 * balances, never what this user holds.
 */
export type V2ReserveTotals = {
  /** When the read started and when it finished (ms since epoch). */
  startedAt: number;
  finishedAt: number;
  /**
   * Liability per denom the proxy holds; undefined where that denom's query
   * failed. A denom the proxy does not hold is absent (its liability is 0).
   */
  liabilities: ReadonlyMap<string, bigint | undefined>;
};

/** How long a Reserve totals read is reused by default. */
export const V2_RESERVE_TOTALS_MAX_AGE_MS = 60_000;
// Gentle on the public LCD, which rate-limits bursts.
const RESERVE_TOTALS_CONCURRENCY = 2;
const PROXY_DENOM_PAGES = 20;
const NATIVE_DENOM = /^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/;

// Proof expiry must come from a recent block on the configured chain. Allow
// ordinary block/transport delay and modest local clock skew, not cached blocks
// old enough to make the proof expire before it reaches the relayer.
const MAX_LATEST_BLOCK_AGE_MS = 120_000;
const MAX_LATEST_BLOCK_FUTURE_SKEW_MS = 30_000;

/** An explicit server response proves that retry cannot duplicate a success. */
export class V2RelayRejectedError extends Error {
  readonly transactionRejected = true;
  /** The chain's own reason, when the relayer's simulation was rejected. */
  reason?: string;
}

/**
 * Chain reasons that mean "the relayer's node is behind", not "this action is
 * invalid": only these are worth sending again. With a funding bundle the
 * funds arrive in the same transaction, so a missing balance is real.
 */
export const isV2LagReason = (reason: string, bundled = false): boolean =>
  /account sequence mismatch|root history|merkle root|nullifier/i.test(
    reason
  ) ||
  (!bundled && /insufficient funds/i.test(reason));

/** What a user reads when the chain rejected an action before it was sent. */
export const friendlyRejection = (reason: string): string => {
  if (
    /ltv|loan.to.value|borrow limit|max borrow|undercollateral|health/i.test(
      reason
    )
  )
    return "This amount is above your borrow limit. Try a little less. Nothing was sent.";
  if (
    /slippage|min(imum)?.?return|less than min|price (moved|impact)/i.test(
      reason
    )
  )
    return "The price moved too much. Try again. Nothing was sent.";
  if (/insufficient funds|insufficient balance|not enough/i.test(reason))
    return "Not enough balance for this action. Nothing was sent.";
  // THORChain's own answer while it halts all contracts ("wasm halted").
  if (/wasm halted/i.test(reason))
    return "THORChain has paused smart contracts for now. Nothing was sent. Please try again later.";
  if (/paused|halt/i.test(reason))
    return "This market is paused right now. Nothing was sent.";
  return `The network rejected this transaction (${reason}). Nothing was sent.`;
};

export const isV2RelayRejectedError = (
  error: unknown
): error is V2RelayRejectedError => error instanceof V2RelayRejectedError;

/** Longest single wait a relayer may ask for before the same request is sent again. */
export const V2_RELAY_BUSY_MAX_WAIT_MS = 20_000;
/** Total time spent waiting on a busy or briefly paused relayer per request. */
export const V2_RELAY_BUSY_BUDGET_MS = 60_000;

/**
 * The relayer answered that nothing was submitted and asked to try again
 * shortly: it is busy, briefly paused, or the network did not accept the
 * transaction. Sending the identical request again cannot duplicate anything.
 */
export class V2RelayBusyError extends V2RelayRejectedError {
  constructor(
    message: string,
    readonly retryAfterMs: number
  ) {
    super(message);
    this.name = "V2RelayBusyError";
  }
}

export const isV2RelayBusyError = (error: unknown): error is V2RelayBusyError =>
  error instanceof V2RelayBusyError;

/** Retry-After in seconds, bounded; a missing or odd header waits 5 s. */
const retryAfterMs = (response: Response): number => {
  const header = response.headers?.get?.("retry-after");
  const seconds =
    header && /^\d{1,4}$/.test(header.trim()) ? Number(header) : 5;
  return (
    Math.min(Math.max(seconds, 1), V2_RELAY_BUSY_MAX_WAIT_MS / 1_000) * 1_000
  );
};

export class V2StaleRootError extends Error {
  constructor(message = "The private proof root expired before submission") {
    super(message);
    this.name = "V2StaleRootError";
  }
}

export const isV2StaleRootError = (error: unknown): boolean =>
  error instanceof V2StaleRootError;

export class V2RelayOutcomeUnknownError extends Error {
  readonly transactionPending = true;
  constructor(readonly transactionHash?: string) {
    super("Transaction status is not yet known. Check before trying again.");
    this.name = "V2RelayOutcomeUnknownError";
  }
}

export const isV2RelayOutcomeUnknownError = (
  error: unknown
): error is V2RelayOutcomeUnknownError =>
  error instanceof V2RelayOutcomeUnknownError ||
  (error !== null &&
    typeof error === "object" &&
    "transactionPending" in error &&
    error.transactionPending === true);

export type V2RelayReconciliation =
  | { status: "confirmed"; txHash: string }
  | { status: "failed"; txHash: string; message: string }
  | { status: "unknown"; txHash?: string };

const transactionHash = (value: unknown): string | undefined =>
  typeof value === "string" && /^[a-fA-F0-9]{64}$/.test(value)
    ? value.toUpperCase()
    : undefined;

// Compare public transaction payloads locally, never send note-specific search
// filters to an indexer. Array order remains significant.
const canonicalJson = (value: unknown): string => {
  const normalize = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(normalize);
    if (input !== null && typeof input === "object") {
      return Object.fromEntries(
        Object.entries(input)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, item]) => [key, normalize(item)])
      );
    }
    return input;
  };
  return JSON.stringify(normalize(value));
};

/**
 * A preflight rejection means the relayer's simulation failed and nothing was
 * broadcast. The common cause is node lag: the client read a newer root or
 * balance than the relayer's node has seen yet (one block apart).
 */
export const isV2PreflightRejectedError = (error: unknown): boolean =>
  error instanceof V2RelayRejectedError && /preflight/i.test(error.message);

/** One block on THORChain; enough for the relayer's node to catch up. */
export const V2_PREFLIGHT_RETRY_DELAY_MS = 6_500;

/** Waits before re-sending the identical message after a preflight rejection. */
export const V2_PREFLIGHT_RESUBMIT_DELAYS_MS = [2_000, 3_000] as const;

/**
 * A preflight rejection right after a funding transfer or merge is almost
 * always the relayer's node one block behind. Nothing was broadcast and the
 * relayer released the request, so the identical message (same proof) is sent
 * again once that node has caught up. This avoids a fresh recovery and a new
 * proof for a one-block lag. A busy or briefly paused relayer is waited out
 * the same way, as long as it asks for it and within a total budget; other
 * errors surface unchanged.
 */
export const relayResubmittingOnPreflight = async (
  send: () => Promise<string>,
  delays: readonly number[] = V2_PREFLIGHT_RESUBMIT_DELAYS_MS,
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms)),
  busyBudgetMs: number = V2_RELAY_BUSY_BUDGET_MS
): Promise<string> => {
  let preflightAttempts = 0;
  let busyWaited = 0;
  for (;;) {
    try {
      return await send();
    } catch (error) {
      if (isV2RelayBusyError(error)) {
        if (busyWaited + error.retryAfterMs > busyBudgetMs) throw error;
        busyWaited += error.retryAfterMs;
        await wait(error.retryAfterMs);
        continue;
      }
      if (
        !isV2PreflightRejectedError(error) ||
        preflightAttempts >= delays.length
      )
        throw error;
      await wait(delays[preflightAttempts]);
      preflightAttempts += 1;
    }
  }
};

export const retryV2StaleRootOnce = async <T>(
  operation: () => Promise<T>,
  refresh: () => Promise<void>,
  preflightDelayMs = V2_PREFLIGHT_RETRY_DELAY_MS
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const preflight = isV2PreflightRejectedError(error);
    if (!isV2StaleRootError(error) && !preflight) throw error;
    // Nothing was submitted: wait for the relayer's node, rebuild from fresh
    // recovery (new root, new proof) and try exactly once more.
    if (preflight)
      await new Promise((resolve) => setTimeout(resolve, preflightDelayMs));
    await refresh();
    return operation();
  }
};

const exactObject = (
  value: unknown,
  label: string
): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Invalid ${label}`);
  }
  return value as Record<string, unknown>;
};

/** Reads the optional signer list; anything unexpected means the primary only. */
const relayerSigners = (
  value: unknown,
  primary: string
): V2RelayerSigner[] | undefined => {
  if (!Array.isArray(value) || value.length < 2 || value.length > 16) return;
  const prefix = primary.slice(0, primary.lastIndexOf("1") + 1);
  if (!prefix) return;
  const signers: V2RelayerSigner[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return;
    const {
      address,
      available,
      estimated_wait_seconds: wait,
    } = entry as Record<string, unknown>;
    if (
      typeof address !== "string" ||
      !address.startsWith(prefix) ||
      !/^[02-9ac-hj-np-z]{38}$/.test(address.slice(prefix.length)) ||
      typeof available !== "boolean" ||
      typeof wait !== "number" ||
      !Number.isSafeInteger(wait) ||
      wait < 0
    )
      return;
    signers.push({ address, available, waitSeconds: wait });
  }
  if (
    signers[0].address !== primary ||
    new Set(signers.map((signer) => signer.address)).size !== signers.length
  )
    return;
  return signers;
};

const uint = (value: unknown, label: string): number => {
  const parsed =
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  if (!Number.isSafeInteger(parsed) || Number(parsed) < 0)
    throw new Error(`Invalid ${label}`);
  return Number(parsed);
};

export class V2ChainClient {
  readonly recoverySource: RecoverySource;
  private deploymentAttestation?: Promise<void>;
  private reserveTotalsRead?: V2ReserveTotals;
  private reserveTotalsPending?: {
    startedAt: number;
    promise: Promise<V2ReserveTotals>;
  };

  constructor(
    readonly deployment: V2DeploymentConfig,
    // Native browser fetch must retain the Window receiver, not this client.
    private readonly fetchImpl: typeof fetch = (...args) =>
      globalThis.fetch(...args)
  ) {
    const contract = createContractRecoverySource(
      this,
      deployment.proxyContract
    );
    this.recoverySource = createResilientRecoverySource({
      contract,
      backendUrl: deployment.backendUrl,
      fetchImpl,
    });
  }

  async queryContractSmart<T>(
    contract: string,
    query: Record<string, unknown>
  ): Promise<T> {
    const encoded = encodeURIComponent(
      base64Encode(utf8(JSON.stringify(query)))
    );
    const response = await readPrivateJson(
      this.fetchImpl,
      `${this.deployment.lcdUrl}/cosmwasm/wasm/v1/contract/${contract}/smart/${encoded}`
    );
    if (!response.ok)
      throw new PrivateReadHttpError("Contract query failed", response.status);
    const body = exactObject(response.body, "contract query response");
    if (typeof body.data === "string") {
      try {
        return JSON.parse(
          new TextDecoder().decode(base64Decode(body.data))
        ) as T;
      } catch {
        throw new Error("Invalid smart-query response encoding");
      }
    }
    return ("data" in body ? body.data : body) as T;
  }

  async contractConfig(): Promise<V2ContractConfig> {
    const [raw] = await Promise.all([
      this.queryContractSmart(this.deployment.proxyContract, { config: {} }),
      this.attestProxyDeployment(),
    ]);
    const value = exactObject(raw, "Private Mode config");
    const registration = exactObject(
      value.registration_fee,
      "registration fee"
    );
    if (
      value.protocol_version !== 2 ||
      typeof value.fee_collector !== "string" ||
      typeof registration.denom !== "string" ||
      typeof registration.amount !== "string" ||
      !/^\d+$/.test(registration.amount) ||
      BigInt(registration.amount) <= 0n
    ) {
      throw new Error("Invalid Private Mode contract configuration");
    }
    if (
      uint(value.subwallet_code_id, "subwallet code id") !==
      this.deployment.expectedSubwalletCodeId
    ) {
      throw new Error("Private Account code ID does not match this build");
    }
    if (typeof value.subwallet_code_checksum !== "string") {
      throw new Error("Invalid Private Account code checksum");
    }
    let subwalletCodeChecksum: string;
    try {
      const checksum = base64Decode(value.subwallet_code_checksum);
      if (checksum.length !== 32) throw new Error("invalid checksum length");
      subwalletCodeChecksum = bytesToHex(checksum);
    } catch {
      throw new Error("Invalid Private Account code checksum");
    }
    if (
      subwalletCodeChecksum !== this.deployment.expectedSubwalletCodeChecksum
    ) {
      throw new Error(
        "Private Account code checksum does not match this build"
      );
    }
    const protocolFeeBps = uint(value.protocol_fee_bps, "protocol fee");
    if (protocolFeeBps > 1_000) throw new Error("Invalid protocol fee limit");
    let feePolicy: Partial<V2ContractConfig> = {};
    if (Object.hasOwn(value, "fee_policy_version")) {
      const version = uint(value.fee_policy_version, "fee policy version");
      const maximum = uint(
        value.maximum_protocol_fee_bps,
        "protocol fee maximum"
      );
      const delay = uint(value.min_fee_update_delay_blocks, "fee update delay");
      if (maximum > 1_000 || protocolFeeBps > maximum || delay === 0)
        throw new Error("Invalid protocol fee policy");
      let pending: V2ContractConfig["pending_protocol_fee_update"] = null;
      if (value.pending_protocol_fee_update !== null) {
        const raw = exactObject(
          value.pending_protocol_fee_update,
          "pending fee update"
        );
        pending = {
          protocol_fee_bps: uint(raw.protocol_fee_bps, "pending protocol fee"),
          activation_height: uint(
            raw.activation_height,
            "fee activation height"
          ),
        };
        if (
          pending.protocol_fee_bps > maximum ||
          pending.activation_height === 0
        )
          throw new Error("Invalid pending protocol fee update");
      }
      feePolicy = {
        fee_policy_version: version,
        pending_protocol_fee_update: pending,
        maximum_protocol_fee_bps: maximum,
        min_fee_update_delay_blocks: delay,
      };
    } else if (
      Object.hasOwn(value, "pending_protocol_fee_update") ||
      Object.hasOwn(value, "maximum_protocol_fee_bps") ||
      Object.hasOwn(value, "min_fee_update_delay_blocks") ||
      Object.hasOwn(value, "fee_rules") ||
      Object.hasOwn(value, "pending_fee_rules_update")
    ) {
      throw new Error("Incomplete protocol fee policy");
    }
    if (
      Object.hasOwn(value, "fee_rules") ||
      Object.hasOwn(value, "pending_fee_rules_update")
    ) {
      if (
        !Object.hasOwn(value, "fee_rules") ||
        !Object.hasOwn(value, "pending_fee_rules_update")
      )
        throw new Error("Incomplete product fee rules");
      const rules =
        value.fee_rules === null ? null : parseFeeRules(value.fee_rules);
      if (rules && !(Number(feePolicy.fee_policy_version) > 0))
        throw new Error("Invalid product fee policy version");
      let pending: V2ContractConfig["pending_fee_rules_update"] = null;
      if (value.pending_fee_rules_update !== null) {
        const raw = exactObject(
          value.pending_fee_rules_update,
          "pending product fee rules"
        );
        const activation_height = uint(
          raw.activation_height,
          "fee rules activation height"
        );
        if (activation_height === 0 || Object.keys(raw).length !== 2)
          throw new Error("Invalid pending product fee rules");
        pending = { rules: parseFeeRules(raw.rules), activation_height };
      }
      feePolicy.fee_rules = rules;
      feePolicy.pending_fee_rules_update = pending;
    }
    return {
      subwallet_code_id: uint(value.subwallet_code_id, "subwallet code id"),
      subwallet_code_checksum: subwalletCodeChecksum,
      fee_collector: value.fee_collector,
      protocol_fee_bps: protocolFeeBps,
      ...feePolicy,
      call_relayer_fee_caps: parseCallRelayerFeeCaps(
        value.call_relayer_fee_caps
      ),
      registration_fee: {
        denom: registration.denom,
        amount: registration.amount,
      },
      root_history_size: uint(value.root_history_size, "root history"),
      max_ciphertext_bytes: uint(
        value.max_ciphertext_bytes,
        "ciphertext limit"
      ),
      max_expiry_delta: uint(value.max_expiry_delta, "expiry limit"),
      protocol_version: 2,
    };
  }

  async quoteCallFees(
    subwallet: string,
    calls: readonly V2Call[],
    config: V2ContractConfig
  ): Promise<V2CallFeeQuote> {
    if (!config.fee_rules || !(Number(config.fee_policy_version) > 0))
      throw new Error("Contract-classified private fees are unavailable");
    assertFeeQueryAddress(
      subwallet,
      fromBech32(this.deployment.proxyContract).prefix
    );
    canonicalCalls(calls);
    const quote = await this.queryContractSmart(this.deployment.proxyContract, {
      quote_call_fees: { subwallet, calls },
    });
    return validateCallFeeQuote(quote, config);
  }

  async attestProxyDeployment(): Promise<void> {
    this.deploymentAttestation ??= (async () => {
      const response = await readPrivateJson(
        this.fetchImpl,
        `${this.deployment.lcdUrl}/cosmwasm/wasm/v1/contract/${this.deployment.proxyContract}`
      );
      if (!response.ok) {
        throw new Error(
          `Unable to attest the Private Mode deployment (${response.status})`
        );
      }
      const body = exactObject(response.body, "contract information");
      const info = exactObject(body.contract_info, "contract information");
      const codeId = uint(info.code_id, "proxy code id");
      const actualAdmin =
        typeof info.admin === "string" && info.admin.length > 0
          ? info.admin
          : undefined;
      if (codeId !== this.deployment.expectedProxyCodeId) {
        throw new Error("Private Reserve code ID does not match this build");
      }
      if (actualAdmin !== this.deployment.expectedProxyAdmin) {
        throw new Error(
          "Redacted proxy upgrade admin does not match this build"
        );
      }
    })();
    try {
      await this.deploymentAttestation;
    } catch (error) {
      this.deploymentAttestation = undefined;
      throw error;
    }
  }

  /**
   * The address a sub created with `salt` gets. Without a contract config it
   * uses the checksum this build expects, which `contractConfig` enforces for
   * every creation: prediction needs no network read.
   */
  predictSubwallet(
    salt: Uint8Array,
    config: Pick<V2ContractConfig, "subwallet_code_checksum"> = {
      subwallet_code_checksum: this.deployment.expectedSubwalletCodeChecksum,
    }
  ): string {
    const prefix = fromBech32(this.deployment.proxyContract).prefix;
    return instantiate2Address(
      hexToBytes(config.subwallet_code_checksum, 32),
      this.deployment.proxyContract,
      salt,
      prefix
    );
  }

  async height(): Promise<bigint> {
    // Some upstream proxies cache this route despite fetch cache directives.
    // The cache key contains only time, never any wallet or private-note data.
    const response = await readPrivateJson(
      this.fetchImpl,
      `${this.deployment.lcdUrl}/cosmos/base/tendermint/v1beta1/blocks/latest?fresh=${Date.now()}`
    );
    if (!response.ok)
      throw new Error(`Unable to read chain height (${response.status})`);
    const body = exactObject(response.body, "latest block");
    const block = exactObject(body.block, "latest block");
    const header = exactObject(block.header, "latest block header");
    if (
      typeof header.height !== "string" ||
      !/^[1-9]\d{0,15}$/.test(header.height) ||
      BigInt(header.height) > BigInt(Number.MAX_SAFE_INTEGER)
    ) {
      throw new Error("Invalid latest block height");
    }
    if (header.chain_id !== this.deployment.chainId) {
      throw new Error("Latest block does not match the Private Mode chain");
    }
    const blockTime =
      typeof header.time === "string" ? Date.parse(header.time) : NaN;
    if (!Number.isFinite(blockTime)) {
      throw new Error("Invalid latest block timestamp");
    }
    const age = Date.now() - blockTime;
    if (
      age > MAX_LATEST_BLOCK_AGE_MS ||
      age < -MAX_LATEST_BLOCK_FUTURE_SKEW_MS
    ) {
      throw new Error(
        "The latest chain block is not recent. Please check your device clock and try again once chain data is current."
      );
    }
    return BigInt(header.height);
  }

  async bankBalance(address: string, denom: string): Promise<bigint> {
    const decoded = fromBech32(address);
    const prefix = fromBech32(this.deployment.proxyContract).prefix;
    if (
      decoded.prefix !== prefix ||
      ![20, 32].includes(decoded.data.length) ||
      !/^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/.test(denom)
    ) {
      throw new Error("Invalid private account balance query");
    }
    const response = await readPrivateJson(
      this.fetchImpl,
      `${this.deployment.lcdUrl}/cosmos/bank/v1beta1/balances/${encodeURIComponent(address)}/by_denom?denom=${encodeURIComponent(denom)}`
    );
    if (!response.ok)
      throw new PrivateReadHttpError(
        "Unable to check private account fee balance",
        response.status
      );
    const body = exactObject(response.body, "bank balance");
    const coin = exactObject(body.balance, "bank balance coin");
    if (
      coin.denom !== denom ||
      typeof coin.amount !== "string" ||
      coin.amount.length > 78 ||
      !/^(0|[1-9]\d*)(?![\s\S])/.test(coin.amount) ||
      BigInt(coin.amount) >= 1n << 256n
    ) {
      throw new Error("Invalid private account fee balance");
    }
    return BigInt(coin.amount);
  }

  /** All native bank balances of one owned sub-wallet (claw-back input). */
  /** Average THORChain block time over the last `blocks` blocks (ms). */
  async blockTimeMs(blocks = 100): Promise<number | undefined> {
    const header = async (height: string) => {
      const response = await readPrivateJson(
        this.fetchImpl,
        `${this.deployment.lcdUrl}/cosmos/base/tendermint/v1beta1/blocks/${height}`,
        10_000
      );
      if (!response.ok) return undefined;
      const block = exactObject(
        exactObject(response.body, "block response").block,
        "block"
      );
      const head = exactObject(block.header, "block header");
      const parsed = Number(head.height);
      return typeof head.time === "string" && Number.isSafeInteger(parsed)
        ? { height: parsed, time: head.time }
        : undefined;
    };
    const latest = await header("latest");
    if (!latest || latest.height <= blocks) return undefined;
    const earlier = await header(String(latest.height - blocks));
    return earlier ? averageBlockTimeMs(latest, earlier) : undefined;
  }

  async bankBalances(address: string): Promise<V2Coin[]> {
    const decoded = fromBech32(address);
    const prefix = fromBech32(this.deployment.proxyContract).prefix;
    if (decoded.prefix !== prefix || ![20, 32].includes(decoded.data.length)) {
      throw new Error("Invalid private account balance query");
    }
    const response = await readPrivateJson(
      this.fetchImpl,
      `${this.deployment.lcdUrl}/cosmos/bank/v1beta1/balances/${encodeURIComponent(address)}?pagination.limit=100`
    );
    if (!response.ok)
      throw new PrivateReadHttpError(
        "Unable to read private account balances",
        response.status
      );
    const body = exactObject(response.body, "bank balances");
    if (!Array.isArray(body.balances) || body.balances.length > 100)
      throw new Error("Invalid private account balances");
    return body.balances.map((entry: unknown) => {
      const coin = exactObject(entry, "bank balance coin");
      if (
        typeof coin.denom !== "string" ||
        !/^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/.test(coin.denom) ||
        typeof coin.amount !== "string" ||
        coin.amount.length > 78 ||
        !/^(0|[1-9]\d*)(?![\s\S])/.test(coin.amount) ||
        BigInt(coin.amount) >= 1n << 256n
      ) {
        throw new Error("Invalid private account balance");
      }
      return { denom: coin.denom, amount: coin.amount };
    });
  }

  /**
   * Every denom the proxy holds: its bank balances, all pages. Identical for
   * every user and never narrowed to what this user holds.
   */
  async proxyDenoms(): Promise<string[]> {
    const denoms = new Set<string>();
    let key: string | undefined;
    for (let page = 0; page < PROXY_DENOM_PAGES; page += 1) {
      const query = new URLSearchParams({ "pagination.limit": "100" });
      if (key) query.set("pagination.key", key);
      const response = await readPrivateJson(
        this.fetchImpl,
        `${this.deployment.lcdUrl}/cosmos/bank/v1beta1/balances/${encodeURIComponent(this.deployment.proxyContract)}?${query}`
      );
      if (!response.ok)
        throw new PrivateReadHttpError(
          "Unable to read the Reserve assets",
          response.status
        );
      const body = exactObject(response.body, "Reserve balances");
      if (!Array.isArray(body.balances) || body.balances.length > 100)
        throw new Error("Invalid Reserve balances");
      for (const entry of body.balances) {
        const coin = exactObject(entry, "Reserve balance");
        if (
          typeof coin.denom !== "string" ||
          !NATIVE_DENOM.test(coin.denom) ||
          typeof coin.amount !== "string" ||
          !/^(0|[1-9]\d{0,77})(?![\s\S])/.test(coin.amount)
        )
          throw new Error("Invalid Reserve balance");
        denoms.add(coin.denom);
      }
      const pagination =
        body.pagination === undefined || body.pagination === null
          ? {}
          : exactObject(body.pagination, "Reserve balances page");
      const next = pagination.next_key;
      if (next === undefined || next === null || next === "")
        return [...denoms].sort();
      if (typeof next !== "string" || next.length > 1_024)
        throw new Error("Invalid Reserve balances page");
      key = next;
    }
    throw new Error("The Reserve holds too many assets to read");
  }

  /** The proxy's public Reserve total for one denom. */
  async reserveLiability(denom: string): Promise<V2ReserveLiability> {
    if (!NATIVE_DENOM.test(denom))
      throw new Error("Invalid Reserve total query");
    const value = exactObject(
      await this.queryContractSmart(this.deployment.proxyContract, {
        reserve_liability: { denom },
      }),
      "Reserve total"
    );
    const amount = (raw: unknown): bigint => {
      if (
        typeof raw !== "string" ||
        !/^(0|[1-9]\d{0,38})(?![\s\S])/.test(raw) ||
        BigInt(raw) >= 1n << 128n
      )
        throw new Error("Invalid Reserve total");
      return BigInt(raw);
    };
    if (value.denom !== denom || typeof value.solvent !== "boolean")
      throw new Error("Invalid Reserve total");
    return {
      denom,
      liability: amount(value.liability),
      balance: amount(value.balance),
      solvent: value.solvent,
    };
  }

  /**
   * The Reserve total of every denom the proxy holds (see V2ReserveTotals).
   * A read is reused while it is at most `maxAgeMs` old and started no earlier
   * than `notBefore` (a caller that needs a read taken after some event, such
   * as an account scan, says so). A failed denom query only marks that denom.
   */
  async reserveTotals(
    options: { maxAgeMs?: number; notBefore?: number } = {}
  ): Promise<V2ReserveTotals> {
    const maxAgeMs = options.maxAgeMs ?? V2_RESERVE_TOTALS_MAX_AGE_MS;
    const notBefore = options.notBefore ?? Number.NEGATIVE_INFINITY;
    const last = this.reserveTotalsRead;
    if (
      last &&
      last.startedAt >= notBefore &&
      Date.now() - last.finishedAt <= maxAgeMs
    )
      return last;
    const pending = this.reserveTotalsPending;
    if (pending && pending.startedAt >= notBefore) return pending.promise;
    const startedAt = Date.now();
    const promise = (async (): Promise<V2ReserveTotals> => {
      const denoms = await retryTransientRead(() => this.proxyDenoms());
      const read = new Map<string, bigint | undefined>();
      let cursor = 0;
      const worker = async () => {
        while (cursor < denoms.length) {
          const denom = denoms[cursor];
          cursor += 1;
          try {
            read.set(
              denom,
              (await retryTransientRead(() => this.reserveLiability(denom)))
                .liability
            );
          } catch {
            read.set(denom, undefined);
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(RESERVE_TOTALS_CONCURRENCY, denoms.length) },
          worker
        )
      );
      const totals: V2ReserveTotals = {
        startedAt,
        finishedAt: Date.now(),
        liabilities: new Map(denoms.map((denom) => [denom, read.get(denom)])),
      };
      if (
        !this.reserveTotalsRead ||
        this.reserveTotalsRead.startedAt <= startedAt
      )
        this.reserveTotalsRead = totals;
      return totals;
    })();
    this.reserveTotalsPending = { startedAt, promise };
    try {
      return await promise;
    } finally {
      if (this.reserveTotalsPending?.promise === promise)
        this.reserveTotalsPending = undefined;
    }
  }

  async relayerMetadata(): Promise<V2RelayerMetadata> {
    if (!this.deployment.backendUrl)
      throw new Error("No Private Mode relayer is configured");
    const response = await readPrivateJson(
      this.fetchImpl,
      `${this.deployment.backendUrl}/v2/relayer`
    );
    if (!response.ok)
      throw new PrivateReadHttpError(
        "Unable to read relayer policy",
        response.status
      );
    const value = exactObject(response.body, "relayer policy");
    if (
      value.version !== 2 ||
      value.chain_id !== this.deployment.chainId ||
      value.proxy_contract !== this.deployment.proxyContract ||
      typeof value.relayer_address !== "string" ||
      !Array.isArray(value.sponsored_actions)
    ) {
      throw new Error("Relayer policy does not match this deployment");
    }
    const rawFees = exactObject(value.minimum_fees, "relayer fees");
    if (Object.keys(rawFees).length > 16)
      throw new Error("Invalid relayer fee policy");
    const fees = new Map<string, bigint>();
    for (const [denom, amount] of Object.entries(rawFees)) {
      if (
        !/^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/.test(denom) ||
        typeof amount !== "string" ||
        amount.length > 39 ||
        !/^[1-9]\d*(?![\s\S])/.test(amount) ||
        BigInt(amount) >= 1n << 128n
      ) {
        throw new Error("Invalid relayer minimum fee");
      }
      fees.set(denom, BigInt(amount));
    }
    if (value.sponsored_actions.some((action) => typeof action !== "string")) {
      throw new Error("Invalid relayer sponsorship policy");
    }
    let registrationFee: V2Coin | undefined;
    if (value.sponsored_actions.includes("register_control")) {
      const registration = exactObject(
        value.registration_fee,
        "sponsored registration fee"
      );
      if (
        typeof registration.denom !== "string" ||
        !/^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/.test(
          registration.denom
        ) ||
        typeof registration.amount !== "string" ||
        !/^[1-9]\d{0,38}(?![\s\S])/.test(registration.amount) ||
        BigInt(registration.amount) >= 1n << 128n ||
        value.registration_sponsor_address !== value.relayer_address
      ) {
        throw new Error(REGISTRATION_SPONSOR_UNAVAILABLE);
      }
      registrationFee = {
        denom: registration.denom,
        amount: registration.amount,
      };
    }
    const signers = relayerSigners(value.relayers, value.relayer_address);
    return {
      address: value.relayer_address,
      primaryAddress: value.relayer_address,
      ...(signers && { signers }),
      minimumFees: fees,
      sponsoredActions: new Set(value.sponsored_actions as string[]),
      maxMessageBytes: uint(value.max_message_bytes, "relayer message limit"),
      maxCalls: uint(value.max_calls, "relayer call limit"),
      fundingBundles:
        Array.isArray(value.bundles) &&
        value.bundles.some(
          (bundle) =>
            Array.isArray(bundle) &&
            bundle.length === 2 &&
            bundle[0] === "transfer_to_subwallet" &&
            bundle[1] === "call_subwallet"
        ),
      ...(registrationFee && {
        registrationFee,
        registrationSponsorAddress: value.relayer_address,
      }),
    };
  }

  async relay(
    prepared: PreparedV2Execution,
    metadata: V2RelayerMetadata
  ): Promise<string> {
    if (!this.deployment.backendUrl)
      throw new Error("No Private Mode relayer is configured");
    const body = JSON.stringify(
      prepared.bundle
        ? { msgs: prepared.bundle, relayer_address: metadata.address }
        : { msg: prepared.msg, relayer_address: metadata.address }
    );
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const expired = new Promise<never>((_, reject) => {
      timeout = setTimeout(() => {
        reject(new V2RelayOutcomeUnknownError());
        controller.abort();
      }, 90_000);
    });
    let response: Response;
    let value: Record<string, unknown>;
    try {
      const result = await Promise.race([
        expired,
        (async () => {
          const response = await this.fetchImpl(
            `${this.deployment.backendUrl}/v2/${prepared.bundle ? "relay-bundle" : "relay"}`,
            {
              method: "POST",
              credentials: "omit",
              signal: controller.signal,
              headers: { "content-type": "application/json" },
              body,
            }
          );
          return {
            response,
            value: exactObject(await response.json(), "relay response"),
          };
        })(),
      ]);
      ({ response, value } = result);
    } catch {
      // A lost HTTP response is not evidence that the transaction failed.
      throw new V2RelayOutcomeUnknownError();
    } finally {
      clearTimeout(timeout);
    }
    const hash = transactionHash(value.transaction_hash);
    if (response.status === 200 && value.ok === true && hash) return hash;
    if (value.code === "STALE_ROOT" && response.status === 409 && !hash) {
      throw new V2StaleRootError();
    }
    if (value.code === "STALE_FEE_POLICY" && response.status === 409 && !hash) {
      throw Object.assign(
        new V2RelayRejectedError(
          "Private transaction fees changed. Review the updated fees and confirm again."
        ),
        { code: "PRIVATE_FEE_REVIEW_REQUIRED" }
      );
    }
    if (
      value.code === "RELAY_OUTCOME_UNKNOWN" ||
      value.code === "RELAY_IN_PROGRESS" ||
      value.code === "RELAY_CONFLICT"
    ) {
      if (hash) {
        // The relayer's receipt index can fail after a successful broadcast.
        // Verify the exact submitted transaction through the existing read-only
        // fallback before interrupting the active user intent (e.g. after a
        // preparatory Reserve merge). This never repeats the relay POST.
        let outcome: V2RelayReconciliation;
        try {
          outcome = await this.reconcileRelay(prepared, metadata, hash);
        } catch {
          throw new V2RelayOutcomeUnknownError(hash);
        }
        if (outcome.status === "confirmed") return outcome.txHash;
        if (outcome.status === "failed")
          throw new V2RelayRejectedError(outcome.message);
      }
      throw new V2RelayOutcomeUnknownError(hash);
    }
    // Nothing was submitted and the relayer asks to come back shortly: it is
    // busy, briefly paused, or the network did not take the transaction.
    if (
      (response.status === 503 &&
        value.code === "RELAY_PREFLIGHT_UNAVAILABLE") ||
      response.status === 429
    ) {
      throw new V2RelayBusyError(
        typeof value.error === "string" && value.error.length <= 200
          ? value.error
          : "The relayer is busy. No transaction was submitted.",
        retryAfterMs(response)
      );
    }
    if (
      (response.status === 503 &&
        value.code === "REGISTRATION_SPONSOR_UNAVAILABLE") ||
      (response.status === 422 &&
        (value.code === "RELAY_REJECTED" ||
          value.code === "GAS_POLICY_LIMIT")) ||
      [400, 401, 403, 404, 413, 415].includes(response.status)
    ) {
      const reason =
        value.code === "RELAY_REJECTED" &&
        typeof value.reason === "string" &&
        value.reason.length <= 200
          ? value.reason
          : undefined;
      // A real rejection reads plainly and fails at once; a lagging node
      // keeps the preflight wording, so the caller may send it again.
      const rejected = new V2RelayRejectedError(
        value.code === "REGISTRATION_SPONSOR_UNAVAILABLE"
          ? REGISTRATION_SPONSOR_UNAVAILABLE
          : reason && !isV2LagReason(reason, !!prepared.bundle)
            ? friendlyRejection(reason)
            : typeof value.error === "string"
              ? value.error
              : "No transaction was submitted. Please review and try again."
      );
      if (reason) rejected.reason = reason;
      throw rejected;
    }
    throw new V2RelayOutcomeUnknownError(hash);
  }

  async reconcileRelay(
    prepared: PreparedV2Execution,
    metadata: Pick<V2RelayerMetadata, "address">,
    submittedHash?: string
  ): Promise<V2RelayReconciliation> {
    const hash = transactionHash(submittedHash);
    const unknown: V2RelayReconciliation = {
      status: "unknown",
      ...(hash ? { txHash: hash } : {}),
    };
    const read = async (path: string) => {
      const response = await readPrivateJson(
        this.fetchImpl,
        `${this.deployment.lcdUrl}${path}`,
        10_000
      );
      if (!response.ok) throw new Error("Receipt unavailable");
      return exactObject(response.body, "transaction receipt");
    };
    const inspect = (
      tx: unknown,
      receipt: unknown
    ): V2RelayReconciliation | undefined => {
      try {
        const response = exactObject(receipt, "receipt");
        const receiptHash = transactionHash(response.txhash);
        if (!receiptHash || (hash && receiptHash !== hash)) return;
        if (uint(response.height, "height") < 1) return;
        const code = uint(response.code, "transaction code");
        const transaction = exactObject(tx, "transaction");
        const body = exactObject(transaction.body, "transaction body");
        // A funding bundle is one transaction with its messages in order.
        const expected = prepared.bundle ?? [prepared.msg];
        if (
          !Array.isArray(body.messages) ||
          body.messages.length !== expected.length
        )
          return;
        for (const [index, entry] of body.messages.entries()) {
          const execute = exactObject(entry, "execute");
          if (
            execute["@type"] !== "/cosmwasm.wasm.v1.MsgExecuteContract" ||
            execute.sender !== metadata.address ||
            execute.contract !== this.deployment.proxyContract ||
            canonicalJson(execute.funds ?? []) !==
              canonicalJson(prepared.bundle ? [] : prepared.funds)
          )
            return;
          let message = execute.msg;
          if (typeof message === "string") {
            const encoded = message;
            try {
              message = JSON.parse(encoded);
            } catch {
              message = JSON.parse(
                new TextDecoder().decode(base64Decode(encoded))
              );
            }
          }
          if (canonicalJson(message) !== canonicalJson(expected[index])) return;
        }
        return code === 0
          ? { status: "confirmed", txHash: receiptHash }
          : {
              status: "failed",
              txHash: receiptHash,
              message:
                "Transaction failed on-chain. Review the action before trying again.",
            };
      } catch {
        return;
      }
    };
    const readRpcReceipt = async (): Promise<V2RelayReconciliation> => {
      const rpc = this.deployment.receiptRpcUrl;
      if (!rpc) return unknown;
      const readRpc = async (path: string) => {
        const response = await readPrivateJson(
          this.fetchImpl,
          `${rpc}${path}`,
          10_000
        );
        if (!response.ok) throw new Error("Receipt RPC unavailable");
        const body = exactObject(response.body, "receipt RPC response");
        if (body.error) throw new Error("Receipt RPC lookup failed");
        return exactObject(body.result, "receipt RPC result");
      };
      const inspectRpc = async (value: unknown) => {
        try {
          const result = exactObject(value, "RPC transaction");
          if (typeof result.tx !== "string") return;
          const bytes = base64Decode(result.tx);
          const digest = bytesToHex(
            new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))
          ).toUpperCase();
          if (digest !== transactionHash(result.hash)) return;
          const tx = signers.cosmos.decodeTxRaw(bytes);
          if (tx.body.messages.length < 1 || tx.body.messages.length > 2)
            return;
          if (
            tx.body.messages.some(
              (message) =>
                message.typeUrl !== "/cosmwasm.wasm.v1.MsgExecuteContract"
            )
          )
            return;
          const outcome = exactObject(
            result.tx_result,
            "RPC transaction outcome"
          );
          return inspect(
            {
              body: {
                messages: tx.body.messages.map((message) => {
                  const execute = signers.cosmos.MsgExecuteContract.decode(
                    message.value
                  );
                  return {
                    "@type": message.typeUrl,
                    sender: execute.sender,
                    contract: execute.contract,
                    funds: execute.funds,
                    msg: JSON.parse(new TextDecoder().decode(execute.msg)),
                  };
                }),
              },
            },
            { txhash: digest, height: result.height, code: outcome.code }
          );
        } catch {
          return;
        }
      };
      try {
        if (hash)
          return (
            (await inspectRpc(await readRpc(`/tx?hash=0x${hash}`))) ?? unknown
          );
        for (let page = 1; page <= 3; page++) {
          // Identical public, proxy-wide search for every user. Match locally.
          const query = new URLSearchParams({
            query: JSON.stringify(
              `wasm._contract_address='${this.deployment.proxyContract}'`
            ),
            per_page: "100",
            page: String(page),
            order_by: JSON.stringify("desc"),
          });
          const result = await readRpc(`/tx_search?${query}`);
          if (!Array.isArray(result.txs)) return unknown;
          for (const candidate of result.txs.slice(0, 100)) {
            const outcome = await inspectRpc(candidate);
            if (outcome?.status === "confirmed") return outcome;
          }
          if (result.txs.length < 100) return unknown;
        }
      } catch {
        // A lookup failure is never permission to submit again.
      }
      return unknown;
    };
    try {
      if (submittedHash) {
        if (!hash) return unknown;
        const result = await read(`/cosmos/tx/v1beta1/txs/${hash}`);
        return inspect(result.tx, result.tx_response) ?? readRpcReceipt();
      }
      // The same public proxy-wide query for all users; match locally. A bounded
      // search miss is unknown, never permission to create a fresh transaction.
      for (let page = 1; page <= 3; page++) {
        const query = new URLSearchParams({
          query: `wasm._contract_address='${this.deployment.proxyContract}'`,
          limit: "100",
          page: String(page),
          order_by: "ORDER_BY_DESC",
        });
        const result = await read(`/cosmos/tx/v1beta1/txs?${query}`);
        if (!Array.isArray(result.txs) || !Array.isArray(result.tx_responses))
          return readRpcReceipt();
        for (let index = 0; index < Math.min(result.txs.length, 100); index++) {
          const matched = inspect(
            result.txs[index],
            result.tx_responses[index]
          );
          if (matched?.status === "confirmed") return matched;
        }
        // A failed replay cannot prove the original hashless attempt failed.
        // A short page may be pruned or incompletely indexed, not a full tail.
        // Only exact success can resolve a hashless search.
        if (result.txs.length < 100) return readRpcReceipt();
      }
    } catch {
      /* Network/indexer failure leaves the original receipt pending. */
    }
    return readRpcReceipt();
  }
}
